import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Card, CardContent } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { 
  MapPin, Mountain, Cloud, Trees, Waves, 
  Compass, History, Search, AlertTriangle 
} from 'lucide-react'
import type { LocationResult } from '@creanizer/types'

interface LocationResultTabsProps {
  location: LocationResult
}

export function LocationResultTabs({ location }: LocationResultTabsProps) {
  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid grid-cols-5 w-full">
        <TabsTrigger value="basic">基本情報</TabsTrigger>
        <TabsTrigger value="geography">地理・地形</TabsTrigger>
        <TabsTrigger value="environment">環境・気候</TabsTrigger>
        <TabsTrigger value="wildlife">野生動植物</TabsTrigger>
        <TabsTrigger value="history">歴史・伝承</TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="space-y-4">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label className="text-base font-semibold">概要</Label>
              <p className="mt-2 text-sm">{location.basicInfo.summary}</p>
            </div>
            
            <div>
              <Label className="text-base font-semibold">詳細説明</Label>
              <p className="mt-2 text-sm whitespace-pre-wrap">{location.basicInfo.description}</p>
            </div>
            
            <div>
              <Label className="text-base font-semibold">雰囲気・印象・意義</Label>
              <p className="mt-2 text-sm whitespace-pre-wrap">{location.basicInfo.atmosphereAndSignificance}</p>
            </div>
            
            {location.basicInfo.accessibility && (
              <div>
                <Label className="text-base font-semibold">アクセス情報</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      難易度: {location.basicInfo.accessibility.difficulty}/10
                    </Badge>
                  </div>
                  {location.basicInfo.accessibility.routes && (
                    <div>
                      <p className="text-sm font-medium">アクセスルート:</p>
                      <ul className="list-disc list-inside text-sm ml-2">
                        {location.basicInfo.accessibility.routes.map((route, i) => (
                          <li key={i}>{route}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {location.basicInfo.accessibility.warnings && (
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        注意事項:
                      </p>
                      <ul className="list-disc list-inside text-sm ml-2 text-orange-600">
                        {location.basicInfo.accessibility.warnings.map((warning, i) => (
                          <li key={i}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="geography" className="space-y-4">
        <Card>
          <CardContent className="pt-6 space-y-4">
            {location.geography.terrain && (
              <div>
                <Label className="text-base font-semibold">地形</Label>
                <div className="mt-2 space-y-1">
                  <p className="text-sm">主要地形: {location.geography.terrain.primary}</p>
                  {location.geography.terrain.secondary && (
                    <p className="text-sm">副次地形: {location.geography.terrain.secondary.join('、')}</p>
                  )}
                  <p className="text-sm">標高: {location.geography.terrain.elevation}</p>
                  <p className="text-sm">面積: {location.geography.terrain.area}</p>
                  {location.geography.terrain.geologicalFeatures && (
                    <div>
                      <p className="text-sm font-medium">地質学的特徴:</p>
                      <ul className="list-disc list-inside text-sm ml-2">
                        {location.geography.terrain.geologicalFeatures.map((feat, i) => (
                          <li key={i}>{feat}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {location.geography.features && location.geography.features.length > 0 && (
              <div>
                <Label className="text-base font-semibold">特徴</Label>
                <div className="mt-2 space-y-2">
                  {location.geography.features.map((feature, i) => (
                    <div key={i} className="border-l-2 border-green-500 pl-3">
                      <p className="font-medium text-sm">{feature.name}</p>
                      <p className="text-xs text-gray-600">{feature.type}</p>
                      <p className="text-sm mt-1">{feature.description}</p>
                      {feature.significance && (
                        <p className="text-xs text-purple-600 mt-1">{feature.significance}</p>
                      )}
                      {feature.legendaryStatus && (
                        <Badge className="mt-1" variant="secondary">伝説的</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {location.geography.adjacentAreas && (
              <div>
                <Label className="text-base font-semibold">隣接地域</Label>
                <p className="mt-2 text-sm whitespace-pre-wrap">{location.geography.adjacentAreas}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="environment" className="space-y-4">
        <Card>
          <CardContent className="pt-6 space-y-4">
            {location.environment.climate && (
              <div>
                <Label className="text-base font-semibold">気候</Label>
                <div className="mt-2 space-y-1 text-sm">
                  <p>タイプ: {location.environment.climate.type}</p>
                  <p>平均気温: {location.environment.climate.averageTemperature}</p>
                  <p>気温範囲: {location.environment.climate.temperatureRange}</p>
                  <p>湿度: {location.environment.climate.humidity}</p>
                  <p>降水量: {location.environment.climate.precipitation}</p>
                  <p>風のパターン: {location.environment.climate.windPatterns}</p>
                  {location.environment.climate.extremeWeather && (
                    <div>
                      <p className="font-medium">極端な天候:</p>
                      <ul className="list-disc list-inside ml-2">
                        {location.environment.climate.extremeWeather.map((weather, i) => (
                          <li key={i}>{weather}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            
            {location.environment.sounds && (
              <div>
                <Label className="text-base font-semibold">音</Label>
                <div className="mt-2 space-y-2 text-sm">
                  {location.environment.sounds.dominant && (
                    <div>
                      <p className="font-medium">支配的な音:</p>
                      <p className="ml-2">{location.environment.sounds.dominant.join('、')}</p>
                    </div>
                  )}
                  {location.environment.sounds.night && (
                    <div>
                      <p className="font-medium">夜の音:</p>
                      <p className="ml-2">{location.environment.sounds.night.join('、')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {location.environment.naturalHazards && location.environment.naturalHazards.length > 0 && (
              <div>
                <Label className="text-base font-semibold flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  自然災害
                </Label>
                <div className="mt-2 space-y-2">
                  {location.environment.naturalHazards.map((hazard, i) => (
                    <div key={i} className="border-l-2 border-red-500 pl-3">
                      <p className="font-medium text-sm">{hazard.type}</p>
                      <p className="text-xs text-gray-600">
                        頻度: {hazard.frequency} / 深刻度: {hazard.severity}
                      </p>
                      {hazard.warningSign && (
                        <p className="text-sm mt-1">前兆: {hazard.warningSign.join('、')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="wildlife" className="space-y-4">
        <Card>
          <CardContent className="pt-6 space-y-4">
            {location.wildlife.fauna && (
              <div>
                <Label className="text-base font-semibold">動物相</Label>
                <div className="mt-2 space-y-3">
                  {location.wildlife.fauna.apex && location.wildlife.fauna.apex.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-600">頂点捕食者</p>
                      {location.wildlife.fauna.apex.map((apex, i) => (
                        <div key={i} className="mt-2 border-l-2 border-red-500 pl-3">
                          <p className="font-medium text-sm">{apex.name}</p>
                          <p className="text-sm">{apex.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="destructive">危険度: {apex.dangerLevel}/10</Badge>
                            <span className="text-xs text-gray-600">縄張り: {apex.territory}</span>
                          </div>
                          {apex.avoidanceTips && (
                            <p className="text-xs text-orange-600 mt-1">
                              回避方法: {apex.avoidanceTips.join('、')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {location.wildlife.fauna.common && location.wildlife.fauna.common.length > 0 && (
                    <div>
                      <p className="text-sm font-medium">一般的な動物</p>
                      {location.wildlife.fauna.common.map((animal, i) => (
                        <div key={i} className="mt-2 border-l-2 border-gray-300 pl-3">
                          <p className="font-medium text-sm">{animal.name}</p>
                          <p className="text-sm">{animal.description}</p>
                          <p className="text-xs text-gray-600">
                            群れ: {animal.groupSize} / 活動: {animal.activity}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {location.wildlife.flora && (
              <div>
                <Label className="text-base font-semibold">植物相</Label>
                <div className="mt-2 space-y-3">
                  {location.wildlife.flora.dominant && location.wildlife.flora.dominant.length > 0 && (
                    <div>
                      <p className="text-sm font-medium">主要植物</p>
                      {location.wildlife.flora.dominant.map((plant, i) => (
                        <div key={i} className="mt-2 border-l-2 border-green-500 pl-3">
                          <p className="font-medium text-sm">{plant.name}</p>
                          <p className="text-sm">{plant.description}</p>
                          {plant.uses && (
                            <p className="text-xs text-green-600">用途: {plant.uses.join('、')}</p>
                          )}
                          {plant.dangers && (
                            <p className="text-xs text-red-600">危険: {plant.dangers.join('、')}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {location.wildlife.ecosystem && (
              <div>
                <Label className="text-base font-semibold">生態系</Label>
                <div className="mt-2 space-y-1 text-sm">
                  <p>バランス: {location.wildlife.ecosystem.balance}</p>
                  {location.wildlife.ecosystem.foodChain && (
                    <p>食物連鎖: {location.wildlife.ecosystem.foodChain.join(' → ')}</p>
                  )}
                  {location.wildlife.ecosystem.threats && (
                    <p className="text-orange-600">脅威: {location.wildlife.ecosystem.threats.join('、')}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="history" className="space-y-4">
        <Card>
          <CardContent className="pt-6 space-y-4">
            {location.history.origin && (
              <div>
                <Label className="text-base font-semibold">起源</Label>
                <div className="mt-2 space-y-1 text-sm">
                  <p>形成過程: {location.history.origin.formation}</p>
                  <p>年代: {location.history.origin.age}</p>
                  {location.history.origin.mythicalOrigin && (
                    <p className="italic">神話的起源: {location.history.origin.mythicalOrigin}</p>
                  )}
                </div>
              </div>
            )}
            
            {location.history.timeline && location.history.timeline.length > 0 && (
              <div>
                <Label className="text-base font-semibold">年表</Label>
                <div className="mt-2 space-y-2">
                  {location.history.timeline.map((event, i) => (
                    <div key={i} className="border-l-2 border-gray-400 pl-3">
                      <p className="font-medium text-sm">{event.era} - {event.event}</p>
                      <p className="text-sm mt-1">{event.description}</p>
                      <p className="text-xs text-gray-600">影響: {event.impact}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {location.history.legends && location.history.legends.length > 0 && (
              <div>
                <Label className="text-base font-semibold">伝説</Label>
                <div className="mt-2 space-y-3">
                  {location.history.legends.map((legend, i) => (
                    <div key={i} className="border-l-2 border-purple-400 pl-3">
                      <p className="font-medium text-sm">「{legend.title}」</p>
                      <p className="text-xs text-gray-600">{legend.period}</p>
                      <p className="text-sm mt-1 italic whitespace-pre-wrap">{legend.story}</p>
                      <p className="text-xs mt-1">真実性: {legend.truthfulness}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {location.history.culturalSignificance && (
              <div>
                <Label className="text-base font-semibold">文化的意義</Label>
                <div className="mt-2 space-y-1 text-sm">
                  {location.history.culturalSignificance.peoples && (
                    <p>関連民族: {location.history.culturalSignificance.peoples.join('、')}</p>
                  )}
                  {location.history.culturalSignificance.traditions && (
                    <p>伝統: {location.history.culturalSignificance.traditions.join('、')}</p>
                  )}
                  {location.history.culturalSignificance.festivals && (
                    <p>祭り: {location.history.culturalSignificance.festivals.join('、')}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  )
}